import { useEffect, useRef } from 'react';

import { resolveHelpTargetElement } from '@heidi/agents';
import { voiceController } from '../controller';
import { applyGuideStepVisuals } from './guideVisuals';
import { useVoiceGuideStore, type GuideStep } from './useVoiceGuideStore';
import { useVoiceHighlightStore } from './useVoiceHighlightStore';

const notifyAdvance = (
  guideId: string,
  title: string,
  completed: GuideStep | null,
  finished: boolean,
  nextStep: GuideStep | null
) => {
  if (finished) {
    useVoiceHighlightStore.getState().clearHighlight();
    voiceController.sendGuideEvent({
      guideId,
      stepId: completed?.id,
      status: 'finished',
      title,
    });
    return;
  }
  voiceController.sendGuideEvent({
    guideId,
    stepId: completed?.id,
    status: 'advanced',
    title,
  });
  applyGuideStepVisuals(nextStep);
};

/** Imperative continue used by the overlay Continue button. */
export const advanceVoiceGuideFromUi = () => {
  const current = useVoiceGuideStore.getState().guide;
  if (!current) return;
  const completed = current.steps[current.stepIndex] ?? null;
  const { finished, nextStep } = useVoiceGuideStore.getState().advanceGuide();
  notifyAdvance(current.id, current.title, completed, finished, nextStep);
};

/**
 * Owns guide step visuals + click-to-advance while a help playbook is active.
 * Mount once with the global voice chrome.
 */
export const useVoiceGuideRunner = () => {
  const guide = useVoiceGuideStore((s) => s.guide);
  const advancingRef = useRef(false);

  useEffect(() => {
    if (!guide) return;
    applyGuideStepVisuals(guide.steps[guide.stepIndex]);
  }, [guide]);

  useEffect(() => {
    if (!guide) return;
    const step = guide.steps[guide.stepIndex];
    if (!step) return;
    const waitFor = step.wait_for ?? 'either';
    if (waitFor === 'speak_next') return;

    const onClickCapture = (event: MouseEvent) => {
      if (advancingRef.current) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Continue is handled by the overlay button (avoid double-advance).
      if (target.closest('[data-testid="voice-guide-continue"]')) return;
      if (!step.highlight) return;
      const el = resolveHelpTargetElement(step.highlight);
      if (!el) return;
      if (el !== target && !el.contains(target)) return;

      advancingRef.current = true;
      // Let the real click land (open menu / select style), then advance.
      window.setTimeout(() => {
        advanceVoiceGuideFromUi();
        advancingRef.current = false;
      }, 180);
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [guide]);
};
